import React, { useContext, useEffect } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBRadio,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge,
  MDBSpinner,
  MDBIcon
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useConfig } from '../config/ConfigProvider';
import { useCompanyBooths } from '../hooks/useCompanyBooths';
import { DevContext } from '../../../../App';

export default function BoothLocationStep({ onComplete, stepId, companyId, eventId }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, handleStepDataChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields } = useValidation();
  const { getStepContent } = useConfig();
  const { isDevMode } = useContext(DevContext);

  // Use the company booths hook - MUST be called before any conditional returns
  const {
    booths,
    isLoading,
    error: boothsError,
    fetchBooths,
    hasMultipleBooths,
    getBoothDisplayName
  } = useCompanyBooths(companyId, eventId);

  // Get content from configuration - with safe fallbacks
  const stepHeading = getStepContent?.('booth_location', 'heading') || t('Standort der Aktion');
  const stepDescription = getStepContent?.('booth_location', 'description') || t('Ihre Aktion findet an einem Ihrer gebuchten Stände statt.');
  const singleBoothInfo = getStepContent?.('booth_location', 'singleBoothInfo') || t('Diese Aktion wird an diesem Stand stattfinden.');
  const multipleBoothsPrompt = getStepContent?.('booth_location', 'multipleBoothsPrompt') || t('Sie haben mehrere Stände gebucht. Bitte wählen Sie einen aus.');
  const noBoothsWarning = getStepContent?.('booth_location', 'noBoothsWarning') || t('Keine gebuchten Stände gefunden.');

  // Fetch booths on mount
  useEffect(() => {
    if (companyId && eventId) {
      fetchBooths();
    }
  }, [companyId, eventId, fetchBooths]);

  // Auto-select booth if only one is available
  useEffect(() => {
    if (booths.length === 1 && !formData.companyBooth) {
      const booth = booths[0];
      // Only auto-select if booth has a valid boothNumber
      if (booth.boothNumber && booth.boothNumber.trim() !== '') {
        handleStepDataChange({
          companyBooth: booth.boothNumber,
          _selectedBoothId: booth.id
        });
      }
    }
  }, [booths, formData.companyBooth, handleStepDataChange]);

  const handleBoothChange = (boothId) => {
    // Find the selected booth
    const selectedBooth = booths.find(b => b.id === boothId);

    if (selectedBooth) {
      // Store booth number (not ID) in companyBooth as per GraphQL schema
      // Also store booth ID internally for selection state
      handleStepDataChange({
        companyBooth: selectedBooth.boothNumber || '',
        _selectedBoothId: boothId // Internal field for UI state
      });
    } else {
      // Fallback: just update with the booth ID (shouldn't happen)
      handleFieldChange('companyBooth', boothId);
    }
  };

  // Get validation info from schema - with safe defaults
  const stepValidation = getStepValidation('booth_location') || { isValid: false, errors: [] };
  const requiredFields = getStepRequiredFields('booth_location') || [];

  const handleContinue = () => {
    // Check if booth is selected
    const hasBoothSelected = formData.companyBooth && formData.companyBooth.trim() !== '';

    if (hasBoothSelected && stepValidation.isValid) {
      onComplete?.(stepId);
      goToNextStep();
    }
  };

  const canContinue = formData.companyBooth && formData.companyBooth.trim() !== '' && stepValidation.isValid;

  // Find selected booth - use internal _selectedBoothId or fallback to matching booth number
  const selectedBooth = booths.find(booth =>
    booth.id === formData._selectedBoothId ||
    booth.boothNumber === formData.companyBooth
  );

  // Check if required data is missing - show error state in render instead of early return
  const hasFormContextError = !formData || !handleFieldChange || !handleStepDataChange;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
        <MDBTypography tag="h3" className="mb-4">
          {stepHeading || t('Standort der Aktion')}
        </MDBTypography>

        {/* Form Context Error */}
        {hasFormContextError && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBAlert color="danger">
                <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                {t('Fehler beim Laden des Formulars. Bitte laden Sie die Seite neu.')}
              </MDBAlert>
            </MDBCol>
          </MDBRow>
        )}

        {!hasFormContextError && (
          <>
            <MDBRow className="mb-4">
              <MDBCol>
                <MDBTypography tag="p" className="mb-4 text-muted">
                  {stepDescription || t('Ihre Aktion findet an einem Ihrer gebuchten Stände statt. Bitte wählen Sie aus, an welchem Stand die Aktion stattfinden soll.')}
                </MDBTypography>
              </MDBCol>
            </MDBRow>

        {/* Loading State */}
        {isLoading && (
          <MDBRow className="mb-4">
            <MDBCol className="text-center py-5">
              <MDBSpinner role="status">
                <span className="visually-hidden">{t('Loading')}</span>
              </MDBSpinner>
              <p className="text-muted mt-3">{t('Lade gebuchte Stände')}</p>
            </MDBCol>
          </MDBRow>
        )}

        {/* Error State */}
        {boothsError && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBAlert color="danger">
                <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                {boothsError}
              </MDBAlert>
            </MDBCol>
          </MDBRow>
        )}

        {/* No Booths Found */}
        {!isLoading && !boothsError && booths.length === 0 && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBAlert color="warning">
                <MDBIcon fas icon="exclamation-circle" className="me-2" />
                {noBoothsWarning || t('Es wurden keine gebuchten Stände für dieses Event gefunden. Bitte wenden Sie sich an den Support.')}
              </MDBAlert>
            </MDBCol>
          </MDBRow>
        )}

        {/* Single Booth - Informational Display */}
        {!isLoading && booths.length === 1 && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-success shadow-sm">
                <MDBCardBody className="d-flex align-items-center">
                  <MDBIcon fas icon="map-marker-alt" size="2x" className="text-success me-3" />
                  <div className="flex-grow-1">
                    <MDBTypography tag="h5" className="mb-2">
                      {getBoothDisplayName(booths[0]) || t('Ihr Stand')}
                    </MDBTypography>
                    <p className="mb-0 text-muted">
                      {singleBoothInfo || t('Diese Aktion wird an diesem Stand stattfinden.')}
                    </p>
                  </div>
                  <MDBIcon fas icon="check-circle" size="2x" className="text-success" />
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        )}

        {/* Multiple Booths - Selection */}
        {!isLoading && booths.length > 1 && (
          <>
            <MDBRow className="mb-3">
              <MDBCol>
                <MDBAlert color="info">
                  <MDBIcon fas icon="info-circle" className="me-2" />
                  {multipleBoothsPrompt || t('Sie haben mehrere Stände gebucht. Bitte wählen Sie aus, an welchem Stand diese Aktion stattfinden soll.')}
                </MDBAlert>
              </MDBCol>
            </MDBRow>

            <MDBRow className="mb-4">
              <MDBCol>
                {booths.map((booth) => {
                  // Check if this booth is selected using internal ID or booth number
                  const isSelected = formData._selectedBoothId === booth.id ||
                                     formData.companyBooth === booth.boothNumber;
                  const isMissingBoothNumber = !booth.boothNumber || booth.boothNumber.trim() === '';

                  return (
                    <MDBCard
                      key={booth.id}
                      className={`mb-3 ${isSelected ? 'border-success shadow-3' : isMissingBoothNumber ? 'border-danger opacity-50' : 'border-0 shadow-sm'}`}
                      style={{ borderRadius: '12px', cursor: isMissingBoothNumber ? 'not-allowed' : 'pointer' }}
                      onClick={() => !isMissingBoothNumber && handleBoothChange(booth.id)}
                    >
                      <MDBCardBody className="d-flex align-items-center">
                        <MDBIcon fas icon="map-marker-alt" size="lg" className={`me-3 ${isSelected ? 'text-success' : isMissingBoothNumber ? 'text-danger' : 'text-muted'}`} />

                        <div className="flex-grow-1">
                          <MDBTypography tag="h6" className="mb-1">
                            {getBoothDisplayName(booth) || t('Stand')}
                          </MDBTypography>
                          {isMissingBoothNumber ? (
                            <small className="text-danger">
                              <MDBIcon fas icon="exclamation-triangle" className="me-1" />
                              {t('Booth number missing - please contact the organizer')}
                            </small>
                          ) : booth.status && (
                            <small className="text-muted">
                              {t('Status')}: {booth.status}
                            </small>
                          )}
                        </div>

                        <MDBRadio
                          name="boothLocation"
                          id={`booth-${booth.id}`}
                          value={booth.id}
                          checked={isSelected}
                          onChange={() => !isMissingBoothNumber && handleBoothChange(booth.id)}
                          disabled={isMissingBoothNumber}
                          label=""
                        />
                      </MDBCardBody>
                    </MDBCard>
                  );
                })}
              </MDBCol>
            </MDBRow>
          </>
        )}

        {/* Field Error */}
        {hasFieldError('companyBooth') && (
          <MDBRow className="mb-3">
            <MDBCol>
              <MDBAlert color="danger">
                {getFieldError('companyBooth')}
              </MDBAlert>
            </MDBCol>
          </MDBRow>
        )}

        {/* Validation Debug Info */}
        {isDevMode && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-info">
                <MDBCardBody>
                  <MDBTypography tag="h6" className="mb-3">
                    🔍 Validation Debug Info (Booth Location Step - Dev Mode)
                  </MDBTypography>

                  <div className="mb-2">
                    <strong>Company ID:</strong> {companyId || 'Not provided'}
                  </div>

                  <div className="mb-2">
                    <strong>Event ID:</strong> {eventId || 'Not provided'}
                  </div>

                  <div className="mb-2">
                    <strong>Booths Found:</strong> {booths.length}
                  </div>

                  <div className="mb-2">
                    <strong>Has Multiple Booths:</strong> {hasMultipleBooths() ? 'Yes' : 'No'}
                  </div>

                  <div className="mb-2">
                    <strong>Selected Booth Number (saved to DB):</strong> {formData.companyBooth || 'None'}
                  </div>

                  <div className="mb-2">
                    <strong>Internal Booth ID:</strong> {formData._selectedBoothId || 'None'}
                  </div>

                  {selectedBooth && (
                    <div className="mb-2">
                      <strong>Selected Booth:</strong> {getBoothDisplayName(selectedBooth)}
                    </div>
                  )}

                  <div className="mb-2">
                    <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
                  </div>

                  <div className="mb-2">
                    <strong>Validation Status:</strong>{' '}
                    <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                      {stepValidation.isValid ? 'Valid' : 'Invalid'}
                    </MDBBadge>
                  </div>

                  {stepValidation.errors.length > 0 && (
                    <div className="mb-2">
                      <strong>Schema Errors:</strong>
                      <ul className="mb-0">
                        {stepValidation.errors.map((error, index) => (
                          <li key={index}>
                            <code>{error.field}</code>: {error.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mb-2">
                    <strong>Continue Button:</strong>{' '}
                    <MDBBadge color={canContinue ? 'success' : 'warning'}>
                      {canContinue ? 'Enabled' : 'Disabled'}
                    </MDBBadge>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        )}
          </>
        )}
      </MDBContainer>

      {/* Fixed button container at bottom */}
      {!hasFormContextError && (
        <div
          className="bg-white"
          style={{
            position: 'sticky',
            bottom: 0,
            padding: '1rem',
            marginTop: 'auto',
            zIndex: 10
          }}
        >
          <MDBContainer>
            <MDBRow>
              <MDBCol className="d-flex justify-content-center gap-5">
                <MDBBtn color="secondary" onClick={goToPreviousStep} outline>
                  {t('Back')}
                </MDBBtn>
                <MDBBtn onClick={handleContinue} disabled={!canContinue || isLoading}>
                  {t('Continue')}
                </MDBBtn>
              </MDBCol>
            </MDBRow>
          </MDBContainer>
        </div>
      )}
    </div>
  );
}
